/* ace_annotations.js */

/**
 * @module ace_annotations
 *
 * Helpers for translating validation errors (from json_schema_validator) into
 * Ace gutter annotations + tooltip markers. Errors of type "error" render as
 * the red X icon Ace draws by default in the gutter.
 *
 * The caller (an editor wrapper) owns the marker-ID list: `applyToSession`
 * returns the IDs it added, and `clearSession` takes those IDs back so we
 * don't mutate hidden state on the Ace session object.
 */
const _ = require("lodash");

const ERROR_MARKER_CSS_CLASS = "json_editor_error_marker";

/**
 * Build an Ace Range object spanning a single line/column to one column over.
 * Lazily resolves Ace's Range so this util can be required in a non-browser
 * context (e.g. unit tests); falls back to a plain {start,end} shape there.
 */
const buildSingleColumnRange = (startLine, startColumn, endLine, endColumn) => {
    if (typeof window !== "undefined" && window.ace && window.ace.require) {
        const AceRange = window.ace.require("ace/range").Range;
        return new AceRange(startLine, startColumn, endLine, endColumn);
    }
    return {
        start: {row: startLine, column: startColumn},
        end: {row: endLine, column: endColumn},
    };
};

/**
 * Apply a list of {line,column,message} validation errors to the given Ace
 * edit session. Returns the marker IDs that were added so the caller can
 * pass them to `clearSession` next time around. Pass `priorMarkerIds` to
 * clear any markers from a previous call before drawing new ones.
 *
 * @param {object} editSession
 * @param {Array<{line,column,message}>} validationErrors
 * @param {Array<number>} [priorMarkerIds=[]] marker IDs to clear first
 * @returns {Array<number>} marker IDs added in this pass
 */
const applyToSession = (editSession, validationErrors, priorMarkerIds = []) => {
    clearSession(editSession, priorMarkerIds);
    if (!editSession) {
        return [];
    }

    const aceAnnotations = _.map(validationErrors, (validationError) => ({
        row: validationError.line || 0,
        column: validationError.column || 0,
        text: validationError.message,
        type: "error",
    }));
    editSession.setAnnotations(aceAnnotations);

    const markerIds = [];
    _.each(validationErrors, (validationError) => {
        const row = validationError.line || 0;
        const column = validationError.column || 0;
        const markerRange = buildSingleColumnRange(row, column, row, column + 1);
        // Only call addMarker when running against a real Ace session.
        if (editSession.addMarker && markerRange.start) {
            try {
                const markerId = editSession.addMarker(
                    markerRange,
                    ERROR_MARKER_CSS_CLASS,
                    "text",
                    false
                );
                markerIds.push(markerId);
            } catch (markerError) {
                // best-effort — marker positioning may fall outside the doc on
                // a fallback {line:0,column:0} pointer; ignore.
            }
        }
    });
    return markerIds;
};

/**
 * Remove annotations + the supplied marker IDs from the session.
 *
 * @param {object} editSession
 * @param {Array<number>} [priorMarkerIds=[]]
 */
const clearSession = (editSession, priorMarkerIds = []) => {
    if (!editSession) {
        return;
    }
    if (editSession.clearAnnotations) {
        editSession.clearAnnotations();
    }
    _.each(priorMarkerIds, (markerId) => {
        if (editSession.removeMarker) {
            editSession.removeMarker(markerId);
        }
    });
};

module.exports = {
    applyToSession,
    clearSession,
};
